"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1] as const
    }
  };
  return <section id="hero" data-nav="light" className="relative min-h-[85vh] w-full overflow-hidden border-b border-border bg-background text-foreground flex flex-col justify-center">
      {/* Background Machinery Image with Technical Industrial Lighting */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_mechanical_bull_arena.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_mechanical_bull_arena.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/commercial_mechanical_bull_arena.png-wvc-sizes" alt="Commercial mechanical bull unit installed in heavy-duty arena" loading="eager" decoding="async" className="h-full w-full object-cover object-center brightness-[0.92] contrast-[1.05]" />
        {/* Crisp Architectural Tint Overlay */}
        <div className="absolute inset-0 bg-[hsl(20_10%_9%)]/85 backdrop-blur-[2px]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(20_10%_9%)] via-[hsl(20_10%_9%)]/90 to-transparent" />
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 py-20 sm:px-10 lg:px-16 lg:py-28">
        <div className="max-w-4xl">
          {/* Left Text & Action Column */}
          <motion.div {...animationProps} className="flex flex-col items-start text-left">
            {/* Machine Classification Eyebrow Badge */}
            <div className="mb-6 inline-flex items-center gap-2 border border-white/20 bg-white/10 px-3.5 py-1.5">
              <span className="h-2 w-2 bg-primary shrink-0" />
              <span className="font-mono text-xs sm:text-sm font-semibold tracking-wider text-[hsl(30_20%_96%)] uppercase">
                NEW • USED • PARTS • BUYING GUIDES
              </span>
            </div>

            {/* Main Headline (Barlow Condensed bold uppercase) */}
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight uppercase leading-[1.04] text-[hsl(30_20%_96%)] mb-6">
              Mechanical Bulls for Sale
            </h1>

            {/* Concise Technical Subline */}
            <p className="font-sans text-base sm:text-lg lg:text-xl text-[hsl(30_15%_85%)] leading-relaxed max-w-2xl mb-8">
              Looking for a mechanical bull for sale? Mechanical Bull Shop lets
              you compare new and used systems with visible pricing, clear
              product information and direct enquiries, without digging through
              vague listings for basic specifications.
            </p>

            {/* Primary Action Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground font-serif uppercase tracking-wider text-base font-semibold px-8 py-6 h-auto transition-[color,background-color,border-color] duration-200 border border-primary">
                <Link to="#">
                  VIEW CURRENT INVENTORY
                  <ArrowRight className="size-5 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="border-white bg-white text-[hsl(20_10%_9%)] hover:bg-[hsl(30_20%_92%)] font-serif uppercase tracking-wider text-base font-semibold px-7 py-6 h-auto transition-[color,background-color] duration-200">
                <Link to="/MechanicalBullPriceGuidePage">View Price Guide</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}